import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { isGraphQLRequestBody } from '@shared/helpers/graphql-verifiers/graphql-body-checker';
import { AllowedProjectsDepartmentsService } from '@shared/services/allowedProjectDepartments/allowed-projects-departments.service';
import { GraphQLRequestBody } from '@shared/types/graphql-request-type';
import { GraphQLVariables } from '@shared/types/graphql-where-types/graphql-where-types';

export const roleAccessInterceptor: HttpInterceptorFn = (req, next) => {
  const allowedProjectsDepartmentsService = inject(
    AllowedProjectsDepartmentsService,
  );

  if (!isGraphQLRequestBody(req.body)) {
    return next(req);
  }

  const operationName = req.body.operationName;
  const allowedProjectCodes = allowedProjectsDepartmentsService.projectCodes();
  const allowedDepartmentCodes =
    allowedProjectsDepartmentsService.departmentCodes();

  const allowedDepartmentName =
    allowedProjectsDepartmentsService.departmentNames();

  if (!allowedProjectCodes.length && !allowedDepartmentCodes.length) {
    return next(req);
  }

  let modifiedBody: GraphQLRequestBody;

  switch (operationName) {
    case 'manageProjectData':
      if (!allowedProjectCodes.length) {
        return next(req);
      }
      modifiedBody = handleManageProjectData(req.body, allowedProjectCodes);
      break;

    case 'manageProjectDepartments':
    case 'getDepartments':
      if (!allowedProjectCodes.length && !allowedDepartmentCodes.length) {
        return next(req);
      }
      modifiedBody = handleManageProjectDepartments(
        req.body,
        allowedDepartmentName,
      );
      break;

    case 'getProjectsWorkOrderRequired':
      if (!allowedProjectCodes.length) {
        return next(req);
      }
      modifiedBody = handleGetProjectsWorkOrderRequired(
        req.body,
        allowedProjectCodes,
      );
      break;

    case 'getProjectsByDepartment':
      if (!allowedProjectCodes.length) {
        return next(req);
      }
      modifiedBody = handleGetProjectsByDepartment(
        req.body,
        allowedProjectCodes,
      );
      break;
    case 'projectWorkOrders':
      if (!allowedProjectCodes.length) {
        return next(req);
      }
      modifiedBody = handleProjectWorkOrders(req.body, allowedProjectCodes);
      break;

    default:
      return next(req);
  }

  const modifiedReq = req.clone({
    body: modifiedBody,
  });

  return next(modifiedReq);
};

const handleManageProjectData = (
  body: GraphQLRequestBody,
  allowedProjectCodes: string[],
): GraphQLRequestBody => {
  const modifiedBody = { ...body };
  modifiedBody.variables ??= {};

  const variables = modifiedBody.variables as GraphQLVariables;

  variables.where ??= {};

  variables.where = {
    ...variables.where,
    projectCode: {
      in: allowedProjectCodes,
    },
  };

  modifiedBody.variables = variables;
  return modifiedBody;
};

const handleManageProjectDepartments = (
  body: GraphQLRequestBody,
  allowedDepartments: string[],
): GraphQLRequestBody => {
  const modifiedBody = { ...body };

  modifiedBody.variables ??= {};

  const variables = modifiedBody.variables as GraphQLVariables;

  variables.where ??= {};

  variables.where = {
    ...variables.where,
    name: {
      in: allowedDepartments,
    },
  };

  modifiedBody.variables = variables;
  return modifiedBody;
};

const handleGetProjectsWorkOrderRequired = (
  body: GraphQLRequestBody,
  allowedProjectCodes: string[],
): GraphQLRequestBody => {
  const modifiedBody = { ...body };

  modifiedBody.variables ??= {};

  const variables = modifiedBody.variables as GraphQLVariables;

  variables.where ??= {};

  const project = (variables.where['project'] as Record<string, unknown>) ?? {};

  variables.where = {
    ...variables.where,
    project: {
      ...project,
      code: {
        in: allowedProjectCodes,
      },
    },
  };

  modifiedBody.variables = variables;

  return modifiedBody;
};

const handleGetProjectsByDepartment = (
  body: GraphQLRequestBody,
  allowedDepartmentCodes: string[],
): GraphQLRequestBody => {
  const modifiedBody = { ...body };

  modifiedBody.variables ??= {};

  const variables = modifiedBody.variables as GraphQLVariables;

  variables.where ??= {};

  variables.where = {
    ...variables.where,
    code: {
      in: allowedDepartmentCodes,
    },
  };

  modifiedBody.variables = variables;

  return modifiedBody;
};

const handleProjectWorkOrders = (
  body: GraphQLRequestBody,
  allowedProjectCodes: string[],
): GraphQLRequestBody => {
  const modifiedBody = { ...body };

  modifiedBody.variables ??= {};

  const variables = modifiedBody.variables as GraphQLVariables;

  variables.where ??= {};

  const project = (variables.where['project'] as Record<string, unknown>) ?? {};

  variables.where = {
    ...variables.where,
    project: {
      ...project,
      code: {
        in: allowedProjectCodes,
      },
    },
  };

  modifiedBody.variables = variables;

  return modifiedBody;
};
